<template>
  <form name="loginForm">
    <div class="login-form-content">
      <app-input
        label="CPF"
        class="login-input"
        v-model="user.cpf"
        placeholder="11 dígitos, sem traços e pontos"
        @input="handleFormInput('cpf', ...arguments)"
        :errorMessage="getMessage('cpf')"
        v-mask="'###.###.###-##'"
        :inputmode="'numeric'"
      />

      <app-input
        label="Email"
        class="login-input"
        v-model="user.email"
        placeholder="Seu email"
        @input="handleFormInput('email', ...arguments)"
        :errorMessage="getMessage('email')"
      />

      <app-input
        label="Celular"
        class="login-input"
        placeholder="Seu celular de contato"
        v-model="user.phone"
        @input="handleFormInput('phone', ...arguments)"
        :errorMessage="getMessage('phone')"
        v-mask="'(##) #####-####'"
        :inputmode="'numeric'"
      />

      <app-input
        label="CEP"
        class="login-input"
        placeholder="Seu CEP para cobrança"
        v-model="user.zipCode"
        @input="handleFormInput('zipCode', ...arguments)"
        :errorMessage="getMessage('zipCode')"
        v-mask="'#####-###'"
        :inputmode="'numeric'"
      />

      <div class="policy-text">
        <app-checkbox
          class="policy"
          v-model="formInputs.checkBox.isValid"
          @click="toggleCheckBox"
        />

        <p @click="toggleCheckBox">
          Estou de acordo com o
          <a :href="`${institutionalUrl}/termos-de-uso`" class="page-anchor" target="_blank"
            >Termos de uso</a
          >
          e a
          <a
            :href="`${institutionalUrl}/politica-de-privacidade`"
            class="page-anchor"
            target="_blank"
            >Política de Privacidade</a
          >
          do Parcelex.
        </p>
      </div>
      <div v-if=captchaKey>
        <vue-recaptcha
        ref="recaptcha"
        @expired="canSubmit = false"
        @verify="canSubmit = true"
        class="captcha"
        :sitekey="captchaKey"
        :loadRecaptchaScript="true"
        >
        </vue-recaptcha>
      </div>
      <span
        v-if="captchaError && !canSubmit && captchaKey"
        class="captchaError input-error-message">
        Preencha o CAPTCHA para prosseguir.
      </span>
      <verify-button
        @completed="handleCompleted"
        @verify="handleVerify"
        :enabled="isValidForm"
        ref="verify-btn"
        class="login-button"
      />
    </div>
    <div class="login-form-footer">
      <a @click="handleAlreadyHasAccount()" class="page-anchor">
        Já tenho uma conta
      </a>
    </div>
  </form>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { mask } from 'vue-the-mask';
import VueRecaptcha from 'vue-recaptcha';
import UtilMixin from '@/mixins/util';
import globals from '@/globals';
import VerifyButton from '../../../components/common/VerifyButton.vue';
import AppInput from '../../../components/ui/AppInput.vue';
import AppCheckbox from '../../../components/ui/AppCheckbox.vue';

export default {
  name: 'SingUpForm',
  components: {
    AppInput,
    VerifyButton,
    AppCheckbox,
    VueRecaptcha,
  },
  directives: {
    mask,
  },
  mixins: [UtilMixin],
  data: () => ({
    institutionalUrl: null,
    canSubmit: false,
    captchaError: false,
    captchaKey: globals().captchaKey,
    largura: '300px',
    inputCheck: '',
    formInputs: {
      cpf: {
        isValid: false,
        validateFunction: 'isValidCpf',
        errorMessage: 'CPF inválido',
        currentMessage: '',
      },
      email: {
        isValid: false,
        validateFunction: 'isValidEmail',
        errorMessage: 'Email inválido',
        currentMessage: '',
      },
      phone: {
        isValid: false,
        validateFunction: 'isValidPhone',
        errorMessage: 'Celular inválido',
        currentMessage: '',
      },
      zipCode: {
        validateFunction: 'isValidZipCode',
        isValid: false,
        errorMessage: 'CEP inválido',
        currentMessage: '',
      },
      checkBox: {
        isValid: false,
        validateFunction: 'isValidCheckBox',
        errorMessage: 'Marque a caixa',
        currentMessage: '',
      },
    },
  }),
  computed: {
    ...mapState('login', ['user', 'result']),
    isValidForm() {
      let isValid = true;
      const keys = Object.keys(this.formInputs);
      for (let i = 0; i < keys.length; i += 1) {
        const key = keys[i];
        if (!this.formInputs[key].isValid) {
          isValid = false;
          break;
        }
      }
      return isValid;
    },
  },
  methods: {
    ...mapActions('login', ['setUserData', 'getPreApprove', 'clearUserData']),
    handleFormInput(field, value) {
      if (!value) {
        return;
      }
      this.setUserData({ field, value });
      const { validateFunction } = this.formInputs[field];
      this.formInputs[field].isValid = this[validateFunction](value);
      if (this.formInputs[field].currentMessage !== '') {
        this.formInputs[field].currentMessage = '';
      }
    },
    handleErrorMessages() {
      Object.keys(this.formInputs).forEach((key) => {
        if (!this.formInputs[key].isValid) {
          this.formInputs[key].currentMessage = this.formInputs[key].errorMessage;
        }
      });
    },
    getMessage(field) {
      return this.formInputs[field].currentMessage;
    },
    async handleVerify() {
      if (!this.canSubmit && this.captchaKey) {
        this.captchaError = true;
        return;
      }
      if (!this.isValidForm) {
        this.handleErrorMessages();
        return;
      }
      try {
        this.$refs['verify-btn'].cantPreApprove();

        await this.getPreApprove();

        if (this.result === 'APPROVED') {
          this.$refs['verify-btn'].complete(true);
        } else {
          this.$refs['verify-btn'].complete(false);
        }
      } catch (error) {
        if (error.message === 'invalid email') {
          this.formInputs.email.isValid = false;
          this.handleErrorMessages();
          this.$refs['verify-btn'].reset();
        } else {
          this.$refs['verify-btn'].complete(false);
        }
      }
    },
    handleCompleted() {
      this.$router.push(
        this.result === 'APPROVED'
          ? '/login/verificacao/aprovada'
          : '/login/verificacao/negada',
      );
    },
    toggleCheckBox() {
      this.formInputs.checkBox.isValid = !this.formInputs.checkBox.isValid;
    },
    handleAlreadyHasAccount() {
      this.clearUserData();
      this.$router.push('/login');
    },
    resizeCaptcha() {
      if (this.$refs.recaptcha) {
        const width = this.$refs.recaptcha.$el.clientWidth;
        if (width > 302) {
          const scale = width / 302;
          this.$refs.recaptcha.$el.style.transform = `scale(${scale})`;
          this.$refs.recaptcha.$el.style.transformOrigin = '0 0';
        }
      }
    },
  },
  mounted() {
    this.institutionalUrl = globals().institutionalUrl;
    this.resizeCaptcha();
  },
};
</script>

<style lang="scss" scoped>
::v-deep .login-input input {
  display: block;
  width: 100%;
  border-radius: 0.375rem;
  border-color: var(--color-gray-300);
  padding: 8px 12px;
  background-color: white;
  height: 56px;

  &:focus {
    border-color: var(--color-primary);
  }

  @media (min-width: $md) {
    height: 42px;
  }
}

.login-button {
  display: flex;
  justify-content: center;
  align-items: center;
  line-height: 1.5rem;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  color: var(--white);
  background-color: var(--color-primary);
  border-radius: 0.375rem;
  border-color: transparent;
  height: 56px;

  &:hover {
    background-color: var(--color-primary-600) !important;
    border-color: transparent;
  }

  @media (min-width: $md) {
    height: 42px;
  }
}

.login-input {
  margin-bottom: 1.25rem;
}

.login-form-footer {
  margin-top: 1.25rem;
  text-align: center;
}

.page-anchor {
  color: var(--color-primary);
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}

.policy-text {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 24px;
  cursor: default;

  ::v-deep .form-group .checkbox {
    border: 1px solid var(--color-gray-300);

    &.active {
      border: 1px solid var(--color-primary);
    }
  }
}

.captcha {
  margin-bottom: 30px;
}

.captchaError {
  display: flex;
  margin-top: -5px;
  margin-bottom: 20px;
}
</style>
